import { FormControl, FormErrorMessage, FormLabel, Radio, Select } from '@chakra-ui/react'
import { SelectionType } from 'BrokerTsLib/src/enums'
import { useFieldArray, useFormContext } from 'BrokerTsLib/src/rhf'

import { InputGroup } from '@/components/InputGroup'
import { FormBody } from '@/containers/forms/FormBody'
import { FormRow } from '@/containers/forms/FormRow'
import { RadioGroup } from '@/containers/forms/RadioGroup'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useSelections } from '@/hooks'
import { useGetClassificationSelections } from '@/hooks/useWizard'
import { useEffect } from 'react'
import type { Step4FormValues } from '../Step4'
import { PreviousContractNumberForm } from './PreviousContractNumberForm'

export const RespectCreateContractForm = () => {
  const { data: dataCat } = useSelections(SelectionType.FormaSjednani)
  const { insuranceParamsId } = useInsuranceParams()
  const { data: dataClassifications, refetch: refetchClassifications } = useGetClassificationSelections(insuranceParamsId!, { enabled: false, cacheTime: 0 })

  const { register, formState: { errors }, setValue, getValues, control } = useFormContext<Step4FormValues>()
  const meetingRecord = getValues('meetingRecord')
  const { fields } = useFieldArray({ control, name: 'meetingRecord.brokerNumbers' })

  useEffect(() => {
    refetchClassifications()
  }, [])

  return (
    <FormBody>
      <FormControl isInvalid={!!errors.meetingRecord?.startType?.message}>
        <FormLabel>Způsob sjednání</FormLabel>

        {dataCat?.data && <RadioGroup
          {...register('meetingRecord.startType')}
          valueTransform={(value) => value?.toString()}
          onChange={e => setValue('meetingRecord.startType', parseInt(e))}
          isDisabled={!meetingRecord?.paymentEnabled || !meetingRecord?.signatureEnabled}
        >
          {dataCat?.data.map((item) => (
            <Radio value={item.value} key={item.id}>
              {item.description}
            </Radio>
          ))}
        </RadioGroup>
        }
      </FormControl>

      <PreviousContractNumberForm></PreviousContractNumberForm>

      <FormRow>
        <FormControl isInvalid={!!errors.meetingRecord?.brokerNumbers?.message}>
          <FormLabel>Získatelské číslo</FormLabel>

          {fields.map((field, index) => (
            <InputGroup key={field.acquisitionNumber}
              {...register(`meetingRecord.brokerNumbers.${index}.acquisitionNumber`)}
              placeholder="Např. 12348765"
              isDisabled
            />
          ))}
        </FormControl>
      </FormRow>

      {dataClassifications?.data && dataClassifications.data.classificationsEnabled &&
                <>
                  <FormRow>
                    <FormControl isInvalid={!!errors.meetingRecord?.classification?.ziskatel?.message}>
                      <FormLabel>Získatel</FormLabel>

                      {(dataClassifications.data.ziskatele!.length) > 0 &&
                                <Select
                                  {...register('meetingRecord.classification.ziskatel')}
                                  isDisabled={!dataClassifications.data.ziskateleEditable}
                                >
                                  <option value='' disabled>Vyberte</option>
                                  {dataClassifications.data.ziskatele!.map((item) => (
                                    <option value={item.dealerNumber} key={item.dealerNumber}>{item.nameShort}</option>
                                  ))}
                                </Select>
                      }

                      {errors.meetingRecord?.classification?.ziskatel &&
                                <FormErrorMessage>{errors.meetingRecord?.classification?.ziskatel?.message}</FormErrorMessage>
                      }
                    </FormControl>


                    <FormControl isInvalid={!!errors.meetingRecord?.classification?.garant?.message}>
                      <FormLabel>Garant</FormLabel>

                      {(dataClassifications.data.garanti!.length) > 0 &&
                                <Select
                                  {...register('meetingRecord.classification.garant')}
                                  isDisabled={!dataClassifications.data.garantiEditable}
                                >
                                  <option value='' disabled>Vyberte</option>
                                  {dataClassifications.data.garanti!.map((item) => (
                                    <option value={item.dealerNumber} key={item.dealerNumber}>{item.nameShort}</option>
                                  ))}
                                </Select>
                      }

                      {errors.meetingRecord?.classification?.garant &&
                                <FormErrorMessage>{errors.meetingRecord?.classification?.garant?.message}</FormErrorMessage>
                      }
                    </FormControl>

                  </FormRow>

                  <FormRow>
                    <FormControl >
                      <FormLabel>Středisko</FormLabel>

                      {(dataClassifications.data.strediska!.length) > 0 &&
                                <Select
                                  {...register('meetingRecord.classification.stredisko')}
                                  isDisabled={!dataClassifications.data.strediskaEditable}
                                >
                                  <option value='' disabled>Vyberte</option>
                                  {dataClassifications.data.strediska!.map((item) => (
                                    <option value={item.id} key={item.id}>{item.name}</option>
                                  ))}
                                </Select>
                      }

                    </FormControl>


                    <FormControl isInvalid={!!errors.meetingRecord?.classification?.obor?.message}>
                      <FormLabel>Obor</FormLabel>

                      {(dataClassifications.data.obory!.length) > 0 &&
                                <Select
                                  {...register('meetingRecord.classification.obor')}
                                  isDisabled={!dataClassifications.data.oboryEditable}
                                >
                                  <option value='' disabled>Vyberte</option>
                                  {dataClassifications.data.obory!.map((item) => (
                                    <option value={item.code} key={item.code}>{item.name}</option>
                                  ))}
                                </Select>
                      }

                      {errors.meetingRecord?.classification?.obor &&
                                <FormErrorMessage>{errors.meetingRecord?.classification?.obor?.message}</FormErrorMessage>
                      }
                    </FormControl>

                  </FormRow>

                  <FormRow>
                    <FormControl isInvalid={!!errors.meetingRecord?.classification?.spravce?.message}>
                      <FormLabel>Správce</FormLabel>

                      {(dataClassifications.data.spravcove!.length) > 0 &&
                                <Select
                                  {...register('meetingRecord.classification.spravce')}
                                >
                                  <option value='' disabled>Vyberte</option>
                                  {dataClassifications.data.spravcove!.map((item) => (
                                    <option value={item.id} key={item.id}>{item.name}</option>
                                  ))}
                                </Select>
                      }

                      {errors.meetingRecord?.classification?.spravce &&
                                <FormErrorMessage>{errors.meetingRecord?.classification?.spravce?.message}</FormErrorMessage>
                      }
                    </FormControl>


                    <FormControl isInvalid={!!errors.meetingRecord?.classification?.prodejce?.message}>
                      <FormLabel>Prodejce</FormLabel>

                      {(dataClassifications.data.spravcove!.length) > 0 &&
                                <Select
                                  {...register('meetingRecord.classification.prodejce')}
                                >
                                  <option value='' disabled>Vyberte</option>
                                  {dataClassifications.data.spravcove!.map((item) => (
                                    <option value={item.id} key={item.id}>{item.name}</option>
                                  ))}
                                </Select>
                      }

                      {errors.meetingRecord?.classification?.prodejce &&
                                <FormErrorMessage>{errors.meetingRecord?.classification?.prodejce?.message}</FormErrorMessage>
                      }
                    </FormControl>

                  </FormRow>
                </>
      }
    </FormBody >
  )
}